/** @jsxImportSource @emotion/react */
import { EuiBreadcrumb, EuiIcon } from "@inscopix/ideas-eui";
import { useRouteMapContext } from "providers/RouteMapProvider/RouteMapProvider";
import { css } from "@emotion/react";
import ProjectIcon, {
  ProjectIconProps,
} from "components/ProjectIcon/ProjectIcon";
import { useMemo } from "react";
import { ProjectFolderDjango } from "pages/gdt/folders/ProjectFoldersProvider.api";
import { DataTableStoreState } from "pages/gdt/store/DataTableProvider.types";
import { Project } from "graphql/_Types";
import { HeaderBreadcrumbs } from "../HeaderBreadcrumbs";
import { ProjectBreadcrumbsTablePopover } from "./ProjectBreadcrumbsTablesPopover";

interface ProjectBreadcrumbsProps {
  project: Pick<Project, "isDemo"> & ProjectIconProps["project"];
  tables: DataTableStoreState["tables"][number][];
  folders: ProjectFolderDjango[];
  currentTenant: { name: string; key: string };
  selectedTable?: DataTableStoreState["tables"][number];
  isCurrentUsersPersonalSpace?: boolean;
}

const styles = {
  tenantBreadcrumb: css`
    color: #ccc;
    font-weight: 400;
  `,
  projectBreadcrumb: (tableIsSelected: boolean) => css`
    color: ${tableIsSelected ? "#CCC" : "white"};
    font-weight: ${tableIsSelected ? "400" : "700"};
  `,
  folderBreadcrumb: (color: string | null) => css`
    color: ${color !== null ? color : "white"};
    font-weight: 400;
  `,
  selectedTableBreadcrumb: css`
    color: white;
    font-weight: 700;
  `,
  selectedTableKey: css`
    color: grey;
  `,
};

/*
 * Displays breadcrumbs for the project page header
 * Displays the organization, project, folder, and table
 * The organization and project are always displayed
 * The folder and table are only displayed if they are selected
 * Tables can be navigated to by clicking on the table name
 */
export const ProjectBreadcrumbs = ({
  project,
  tables,
  folders,
  currentTenant,
  selectedTable,
  isCurrentUsersPersonalSpace,
}: ProjectBreadcrumbsProps) => {
  const navigateToOrganizationPage = useRouteMapContext(
    (s) =>
      s.routeMap["ORGANIZATION"].dynamicPath({
        tenantKey: currentTenant.key,
      }).navigateTo,
  );

  const navigateToProjectPage = useRouteMapContext((s) =>
    s.routeMap["PROJECT"].dynamicPath({
      tenantKey: currentTenant.key,
      projectKey: project.key,
    }),
  ).navigateTo;

  const popoverContent = useMemo(
    () => (
      <ProjectBreadcrumbsTablePopover
        projectKey={project.key}
        tenantKey={currentTenant.key}
        selectedTable={selectedTable}
        tables={tables}
        folders={folders}
      />
    ),
    [currentTenant.key, folders, project.key, selectedTable, tables],
  );

  // get folder name and color if selected table has a folder
  const selectedTableFolder = useMemo(() => {
    if (selectedTable?.folder != null) {
      return folders.find((folder) => folder.id === selectedTable.folder);
    }
    return null;
  }, [selectedTable, folders]);

  const folderBreadcrumb: EuiBreadcrumb | undefined = selectedTableFolder
    ? {
        text: (
          <span>
            <EuiIcon type="folderOpen" /> {selectedTableFolder.name}
          </span>
        ),
        // show folder color if available
        css: styles.folderBreadcrumb(selectedTableFolder.color),
      }
    : undefined;

  const selectedTableBreadcrumb: EuiBreadcrumb | undefined = selectedTable
    ? {
        text: (
          <span>
            <EuiIcon
              type={
                selectedTable.kind === "analysis"
                  ? "indexMapping"
                  : "tableDensityNormal"
              }
            />{" "}
            {selectedTable.name}{" "}
            <span css={styles.selectedTableKey}>({selectedTable.key})</span>
          </span>
        ),
        css: styles.selectedTableBreadcrumb,
        popoverContent: popoverContent,
      }
    : undefined;

  const breadcrumbs: EuiBreadcrumb[] = [];
  // don't show organization breadcrumb if the project is a demo
  if (!project.isDemo) {
    breadcrumbs.push({
      text: isCurrentUsersPersonalSpace ? "Personal Space" : currentTenant.name,
      onClick: (e) => {
        navigateToOrganizationPage();
      },
      "data-test-subj": "projectBreadcrumbsOrganization",
      css: styles.tenantBreadcrumb,
    });
  }

  breadcrumbs.push({
    text: (
      <span>
        <ProjectIcon
          size="m"
          project={project}
          tenantKey={currentTenant.key}
          style={{ position: "absolute", top: "12px" }}
        />
        <span
          style={{
            // 24px icon + 4px
            paddingLeft: "28px",
          }}
        >
          {project.name}
        </span>
      </span>
    ),
    onClick: (e) => {
      navigateToProjectPage();
    },
    css: styles.projectBreadcrumb(selectedTableBreadcrumb !== undefined),
    "data-test-subj": "projectBreadcrumbsProject",
  });

  if (folderBreadcrumb) {
    breadcrumbs.push(folderBreadcrumb);
  }
  if (selectedTableBreadcrumb) {
    breadcrumbs.push(selectedTableBreadcrumb);
  }
  return <HeaderBreadcrumbs breadcrumbs={breadcrumbs} />;
};
